import {
   addColSpan,
   CellSelection,
   isInTable,
   Rect,
   selectedRect,
   TableMap,
   TableRect,
} from '@tiptap/pm/tables';
import { Node } from '@tiptap/pm/model';
import { EditorState, Transaction } from '@tiptap/pm/state';

/**
 * Relevant attribute of cells for these base commands
 */
export interface CellAttrs {
   colspan: number;
   rowspan: number;
   colwidth: number[] | null;
}

/**
 * Removes the provided `row` from the provided table.
 *
 * The official version of this command has a large bug in
 * in it https://github.com/ProseMirror/prosemirror-tables/issues/136.
 * Since `prosemirror-tables` is no longer being maintained, it won't be
 * fixed any time soon. Duplicating the commands here allows us to fix the
 * issue.
 */
export function removeRow(
   tr: Transaction,
   { map, table, tableStart }: TableRect,
   row: number
): void {
   let rowPos = 0;
   for (let i = 0; i < row; i++) rowPos += table.child(i).nodeSize;
   const nextRow = rowPos + table.child(row).nodeSize;

   const mapFrom = tr.mapping.maps.length;
   tr.delete(rowPos + tableStart, nextRow + tableStart);

   for (let col = 0, index = row * map.width; col < map.width; col++, index++) {
      const pos = map.map[index];
      if (row > 0 && pos == map.map[index - map.width]) {
         // If this cell starts in the row above, simply reduce its rowspan
         const attrs = table.nodeAt(pos)!.attrs as CellAttrs;
         tr.setNodeMarkup(tr.mapping.slice(mapFrom).map(pos + tableStart), null, {
            ...attrs,
            rowspan: attrs.rowspan - 1,
         });
         col += attrs.colspan - 1;
         index += attrs.colspan - 1;
      } else if (row < map.height && pos == map.map[index + map.width]) {
         // Else, if it continues in the row below, it has to be moved down
         const cell = table.nodeAt(pos)!;
         const attrs = cell.attrs as CellAttrs;
         const copy = cell.type.create({ ...attrs, rowspan: cell.attrs.rowspan - 1 }, cell.content);
         const newPos = map.positionAt(row + 1, col, table);
         tr.insert(tr.mapping.slice(mapFrom).map(tableStart + newPos), copy);
         col += attrs.colspan - 1;
         index += cell.attrs.colspan - 1;
      }
   }
}

/**
 * Remove the selected rows from a table.
 *
 * @public
 */
export function deleteRow(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
   if (!isInTable(state)) return false;
   if (dispatch) {
      const rect = selectedRect(state),
         tr = state.tr;
      if (rect.top == 0 && rect.bottom == rect.map.height) return false;
      for (let i = rect.bottom - 1; ; i--) {
         removeRow(tr, rect, i);
         if (i == rect.top) break;
         const table = rect.tableStart ? tr.doc.nodeAt(rect.tableStart - 1) : tr.doc;
         if (!table) {
            throw RangeError('No table found');
         }
         rect.table = table;
         rect.map = TableMap.get(rect.table);
      }
      dispatch(tr);
   }
   return true;
}

/**
 * Determines whether the provided rectangle does not fully contain all cells
 * within it. So, this essentially checks if any cell overlaps the border defined by
 * the rectangle.
 *
 * The negation of this check ensures that the collection of cells in the rectangle
 * is exactly the rectangle.
 */
function cellsOverlapRectangle({ width, height, map }: TableMap, rect: Rect) {
   let indexTop = rect.top * width + rect.left,
      indexLeft = indexTop;
   let indexBottom = (rect.bottom - 1) * width + rect.left,
      indexRight = indexTop + (rect.right - rect.left - 1);
   for (let i = rect.top; i < rect.bottom; i++) {
      if (
         (rect.left > 0 && map[indexLeft] == map[indexLeft - 1]) ||
         (rect.right < width && map[indexRight] == map[indexRight + 1])
      )
         return true;
      indexLeft += width;
      indexRight += width;
   }
   for (let i = rect.left; i < rect.right; i++) {
      if (
         (rect.top > 0 && map[indexTop] == map[indexTop - width]) ||
         (rect.bottom < height && map[indexBottom] == map[indexBottom + width])
      )
         return true;
      indexTop++;
      indexBottom++;
   }
   return false;
}

/**
 * Determines whether the given `cell` is empty
 */
function isEmpty(cell: Node): boolean {
   const c = cell.content;

   return c.childCount == 1 && c.child(0).isTextblock && c.child(0).childCount == 0;
}

/**
 * Merge the selected cells into a single cell. Only available when
 * the selected cells' outline forms a rectangle.
 *
 * @public
 */
export function mergeCells(state: EditorState, dispatch?: (tr: Transaction) => void): boolean {
   const sel = state.selection;
   if (!(sel instanceof CellSelection) || sel.$anchorCell.pos == sel.$headCell.pos) return false;
   const rect = selectedRect(state),
      { map } = rect;
   if (cellsOverlapRectangle(map, rect)) return false;
   if (dispatch) {
      const tr = state.tr;
      const seen: Record<number, boolean> = {};
      let mergedPos: number | undefined;
      let mergedCell: Node | undefined;
      for (let row = rect.top; row < rect.bottom; row++) {
         for (let col = rect.left; col < rect.right; col++) {
            const cellPos = map.map[row * map.width + col];
            const cell = rect.table.nodeAt(cellPos);
            if (seen[cellPos] || !cell) continue;
            seen[cellPos] = true;
            if (mergedPos == null) {
               mergedPos = cellPos;
               mergedCell = cell;
            } else {
               const mapped = tr.mapping.map(cellPos + rect.tableStart);
               tr.delete(mapped, mapped + cell.nodeSize);
            }
         }
      }
      if (mergedPos == null || mergedCell == null) {
         return true;
      }

      tr.setNodeMarkup(mergedPos + rect.tableStart, null, {
         ...addColSpan(
            mergedCell.attrs as CellAttrs,
            mergedCell.attrs.colspan,
            rect.right - rect.left - mergedCell.attrs.colspan
         ),
         rowspan: rect.bottom - rect.top,
      });
      tr.setSelection(new CellSelection(tr.doc.resolve(mergedPos + rect.tableStart)));
      dispatch(tr);
   }
   return true;
}
